/** Demo Mode only - finance exception derivation for the consolidated Finance area.
 * Reuses the existing TriggeredException shape from src/utils/rulesEngine.ts (and is
 * rendered via the existing ExceptionBanner component) rather than inventing a new
 * exception model - these are just finance-specific triggers evaluated directly against
 * the finance dataset rather than the generic evaluateRules() context (which doesn't have
 * a finance-shaped context), matching the existing pattern of scenario-specific severity. */

import { TriggeredException } from '../../utils/rulesEngine';
import {
  FinanceContract, classifyFinancialStatus, computeTotals,
} from '../../mocks/data/financeData';

const WASTE_TARGET_PERCENTAGE_OF_COST_OF_SALES = 9;
const MOBILISATION_MONTHS_GRACE = 6;

export interface FinanceExceptionCard extends TriggeredException {
  id: string;
  contractId?: string;
  scopeLabel: string;
  impact: string;
  linkTo: string;
}

/** Evaluates the standard set of finance exceptions across a set of contracts. Each
 * exception names the issue, the affected scope, its financial impact, and a link to the
 * relevant detail view - no vague/generic language. */
export const evaluateFinanceExceptions = (contracts: FinanceContract[], formatCurrency: (value: number) => string): FinanceExceptionCard[] => {
  const exceptions: FinanceExceptionCard[] = [];

  contracts.forEach((contract) => {
    const budget = computeTotals(contract.scenarios.budget);
    const forecast = computeTotals(contract.scenarios.currentForecast);
    const previousForecast = computeTotals(contract.scenarios.previousForecast);
    const scopeLabel = `${contract.client} - ${contract.contractName}`;
    const linkTo = `/finance-performance/contracts/${contract.id}`;

    if (forecast.labourPercentage - budget.labourPercentage > 2) {
      exceptions.push({
        id: `${contract.id}-labour`, contractId: contract.id, scopeLabel, linkTo,
        ruleCode: 'FIN_LABOUR_ABOVE_BUDGET',
        title: 'Labour cost above budget',
        message: `Labour cost is running at ${forecast.labourPercentage.toFixed(1)}% of revenue, ${(forecast.labourPercentage - budget.labourPercentage).toFixed(1)}pts above the ${budget.labourPercentage.toFixed(1)}% budget.`,
        impact: formatCurrency(forecast.totalLabour - budget.totalLabour),
        severity: forecast.labourPercentage - budget.labourPercentage > 3.5 ? 'Blocking' : 'Warning',
        blocking: forecast.labourPercentage - budget.labourPercentage > 3.5,
      });
    }

    if (budget.grossProfitPercentage - forecast.grossProfitPercentage > 1.5) {
      exceptions.push({
        id: `${contract.id}-foodcost`, contractId: contract.id, scopeLabel, linkTo,
        ruleCode: 'FIN_FOOD_COST_INCREASED',
        title: 'Food cost % increased',
        message: `Gross profit % has fallen from ${budget.grossProfitPercentage.toFixed(1)}% (budget) to ${forecast.grossProfitPercentage.toFixed(1)}% on current forecast, driven by food cost.`,
        impact: formatCurrency(forecast.grossProfit - budget.grossProfit),
        severity: 'Warning',
        blocking: false,
      });
    }

    if (forecast.totalRevenue < budget.totalRevenue * 0.95) {
      exceptions.push({
        id: `${contract.id}-revenue`, contractId: contract.id, scopeLabel, linkTo,
        ruleCode: 'FIN_REVENUE_BELOW_FORECAST',
        title: 'Revenue below budget',
        message: `Current forecast revenue (${formatCurrency(forecast.totalRevenue)}) is ${(((budget.totalRevenue - forecast.totalRevenue) / budget.totalRevenue) * 100).toFixed(1)}% below budget (${formatCurrency(budget.totalRevenue)}).`,
        impact: formatCurrency(forecast.totalRevenue - budget.totalRevenue),
        severity: forecast.totalRevenue < budget.totalRevenue * 0.9 ? 'Blocking' : 'Warning',
        blocking: forecast.totalRevenue < budget.totalRevenue * 0.9,
      });
    }

    if (budget.ebitMarginPercentage - forecast.ebitMarginPercentage > 1) {
      exceptions.push({
        id: `${contract.id}-ebitmargin`, contractId: contract.id, scopeLabel, linkTo,
        ruleCode: 'FIN_EBIT_MARGIN_DETERIORATED',
        title: 'EBIT margin deteriorated',
        message: `EBIT margin has deteriorated ${(budget.ebitMarginPercentage - forecast.ebitMarginPercentage).toFixed(1)}pts versus budget (now ${forecast.ebitMarginPercentage.toFixed(1)}%).`,
        impact: formatCurrency(forecast.ebit - budget.ebit),
        severity: 'Warning',
        blocking: false,
      });
    }

    if (forecast.waste > forecast.totalCostOfSales * (WASTE_TARGET_PERCENTAGE_OF_COST_OF_SALES / 100) * 1.15) {
      exceptions.push({
        id: `${contract.id}-waste`, contractId: contract.id, scopeLabel, linkTo,
        ruleCode: 'FIN_WASTE_ABOVE_TARGET',
        title: 'Waste materially above target',
        message: `Waste is ${formatCurrency(forecast.waste)}, more than 15% above the ${WASTE_TARGET_PERCENTAGE_OF_COST_OF_SALES}% of cost-of-sales target.`,
        impact: formatCurrency(forecast.waste - (forecast.totalCostOfSales * WASTE_TARGET_PERCENTAGE_OF_COST_OF_SALES) / 100),
        severity: 'Warning',
        blocking: false,
      });
    }

    if (Math.abs(forecast.ebit - previousForecast.ebit) > Math.abs(budget.ebit) * 0.15 && Math.abs(budget.ebit) > 0) {
      exceptions.push({
        id: `${contract.id}-forecastchange`, contractId: contract.id, scopeLabel, linkTo,
        ruleCode: 'FIN_FORECAST_CHANGED_SIGNIFICANTLY',
        title: 'Forecast changed significantly since previous version',
        message: `Current forecast EBIT (${formatCurrency(forecast.ebit)}) has moved ${formatCurrency(forecast.ebit - previousForecast.ebit)} versus the previous forecast (${formatCurrency(previousForecast.ebit)}).`,
        impact: formatCurrency(forecast.ebit - previousForecast.ebit),
        severity: 'Info',
        blocking: false,
      });
    }

    if (forecast.ebit > 0 && forecast.ebitMarginPercentage < 4) {
      exceptions.push({
        id: `${contract.id}-belowrequiredmargin`, contractId: contract.id, scopeLabel, linkTo,
        ruleCode: 'FIN_PROFITABLE_BELOW_REQUIRED_MARGIN',
        title: 'Profitable but below required margin',
        message: `EBIT margin of ${forecast.ebitMarginPercentage.toFixed(1)}% is below the 4% minimum required margin, despite being EBIT-positive.`,
        impact: formatCurrency(forecast.ebit),
        severity: 'Info',
        blocking: false,
      });
    }

    if (forecast.totalRevenue > budget.totalRevenue * 1.1 && forecast.ebitMarginPercentage < budget.ebitMarginPercentage - 1) {
      exceptions.push({
        id: `${contract.id}-highrevenuelowebit`, contractId: contract.id, scopeLabel, linkTo,
        ruleCode: 'FIN_HIGH_REVENUE_LOW_EBIT',
        title: 'High revenue, low EBIT margin',
        message: `Revenue is ${formatCurrency(forecast.totalRevenue)} (above budget) but EBIT margin (${forecast.ebitMarginPercentage.toFixed(1)}%) trails budget - check cost pass-through on the additional volume.`,
        impact: formatCurrency(forecast.ebit - budget.ebit),
        severity: 'Info',
        blocking: false,
      });
    }

    const monthsSinceMobilisation = (Date.now() - new Date(contract.mobilisedDate).getTime()) / (1000 * 60 * 60 * 24 * 30);
    if (monthsSinceMobilisation < MOBILISATION_MONTHS_GRACE && forecast.totalRevenue < budget.totalRevenue * 0.9) {
      exceptions.push({
        id: `${contract.id}-mobilisation`, contractId: contract.id, scopeLabel, linkTo,
        ruleCode: 'FIN_NEWLY_MOBILISED_BEHIND_RAMPUP',
        title: 'Newly-mobilised contract behind ramp-up plan',
        message: `Mobilised ${Math.round(monthsSinceMobilisation)} month(s) ago; revenue is tracking ${(((budget.totalRevenue - forecast.totalRevenue) / budget.totalRevenue) * 100).toFixed(1)}% behind the ramp-up plan.`,
        impact: formatCurrency(forecast.totalRevenue - budget.totalRevenue),
        severity: 'Warning',
        blocking: false,
      });
    }
  });

  return exceptions;
};

export const financialStatusColor: Record<ReturnType<typeof classifyFinancialStatus>, 'success' | 'info' | 'warning' | 'error'> = {
  'Ahead of plan': 'success',
  'On plan': 'info',
  'At risk': 'warning',
  'Materially behind plan': 'error',
};
